import { LoadingButton } from "@mui/lab";
import {
	Box,
	DialogTitle,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Tabs from "@repo/legacy/components/shared/Tabs";
import { ProjectPositionPriceRateType } from "@repo/server-api";
import { TabKeys } from "@repo/server-api/types";
import { mapYupErrorsToErrorMap, useBoundDeep } from "@repo/shared/common";
import { useCurrencyFormatter,
	BoundAsyncSelect,
	BoundEuroField,
	BoundHoursField,
	BoundTextField,
	useAsyncSelectAccountingProfileQuery,
	useAsyncSelectStaticListHook } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { useMemo, useState, forwardRef, useImperativeHandle, useEffect } from "react";
import * as Yup from "yup";

import { useSaveProjectAdditionalPositionInformation } from "../hooks/SaveProjectAdditionalPositionInformationHook";
import { calculateTotalPrice, createEmptyPosition } from "../types/ProjectTypes";
import { createSelectedServiceSchema } from "../types/ProjectValidation";

import type { ProjectPosition, ProjectSaleableService } from "../types/ProjectTypes";
import type { ErrorMap } from "@repo/shared/common";

export type DialogProjectPositionProps = Readonly<{
	onSaved: (p: ProjectPosition) => void;
}>;

export type DialogProjectPositionArgs = {
	projectId: string;
	services: ProjectSaleableService[];
	position?: ProjectPosition | null;
};

export type DialogProjectPositionHandle = {
	show: (args: DialogProjectPositionArgs) => void;
};

const DialogProjectPosition = forwardRef<DialogProjectPositionHandle, DialogProjectPositionProps>(
	(props, ref) => {
		const { t } = useTranslation();
		const formatCurrency = useCurrencyFormatter();

		const [open, setOpen] = useState(false);

		const [errors, setErrors] = useState<ErrorMap>({});
		const [projectId, setProjectId] = useState<string>("");
		const [position, setPosition] = useState<ProjectPosition>(createEmptyPosition());
		const [services, setServices] = useState<ProjectSaleableService[]>([]);
		// Bindings
		const useBinding = <TValue,>(selector: (m: ProjectPosition) => TValue) => {
			return useBoundDeep(position, setPosition, selector, errors, setErrors);
		};

		const availableServices = useAsyncSelectStaticListHook<ProjectSaleableService>(services);
		const serviceField = useBinding((p) => p.service);
		const pricePerHourField = useBinding((p) => p.pricePerHour);
		const priceField = useBinding((p) => p.price);
		const hoursField = useBinding((p) => p.hours);
		const descriptionField = useBinding((p) => p.description);

		const accountingProfileField = useBinding((p) => p.accountingProfile);
		const searchAccountingProfileHook = useAsyncSelectAccountingProfileQuery();

		const [tabIndex, setTabIndex] = useState<number>(0);

		const validationSchema = Yup.object().shape({
			service: createSelectedServiceSchema(t).required(
				t("service-is-required", { ns: "validation" }),
			),
		});

		const tab1ValidationSchema = Yup.object().shape({
			hours: Yup.number()
				.min(0.01, t("must-be-greater-than-zero", { ns: "validation" }))
				.required(t("required", { ns: "validation" })),
			pricePerHour: Yup.number()
				.min(0, t("must-be-greater-than-or-equal-zero", { ns: "validation" }))
				.required(t("required", { ns: "validation" })),
		});

		const tab2ValidationSchema = Yup.object().shape({
			price: Yup.number()
				.min(0, t("must-be-greater-than-or-equal-zero", { ns: "validation" }))
				.required(t("required", { ns: "validation" })),
			priceRate: Yup.string().required(t("required", { ns: "validation" })),
		});

		const finalValidationSchema = useMemo(() => {
			if (tabIndex === 0) {
				return validationSchema.concat(tab1ValidationSchema);
			} else {
				return validationSchema.concat(tab2ValidationSchema);
			}
		}, [tabIndex, validationSchema, tab1ValidationSchema, tab2ValidationSchema]);

		const onSaved = (savedPosition: ProjectPosition) => {
			// Insert Id into position
			setPosition(savedPosition);
			props.onSaved(savedPosition);
			setOpen(false);
		};

		const { saveProjectPosition, loading } = useSaveProjectAdditionalPositionInformation({
			projectId,
			onSaved,
		});

		useImperativeHandle(ref, () => ({
			show: (args: DialogProjectPositionArgs): void => {
				setProjectId(args.projectId);
				setServices(args.services);
				availableServices.resetSearch();
				searchAccountingProfileHook.resetSearch();

				console.log("DialogProjectPosition show called with position:", args.position);

				if (args.position) {
					setPosition(args.position);

					// determine tab index based on position data
					if (args.position.hours) {
						setTabIndex(0);
					} else {
						setTabIndex(1);
					}
				} else {
					setPosition(createEmptyPosition());
					setTabIndex(0);
				}

				setErrors({});
				setOpen(true);
			},
		}));

		const handleClose = () => {
			setOpen(false);
			setErrors({});
		};

		const handleSubmit = async () => {
			if (loading) {
				return;
			}

			try {
				await finalValidationSchema.validate(position, { abortEarly: false });
				setErrors({});
				saveProjectPosition(position);
			} catch (err) {
				const errorMap = mapYupErrorsToErrorMap(err);
				setErrors(errorMap);
			}
		};

		useEffect(() => {
			setPosition((prevPosition) => ({
				...prevPosition,
				totalPrice: calculateTotalPrice(prevPosition),
			}));
		}, [position.pricePerHour, position.hours]);

		const allTabs = [
			{
				label: t("hourly-rate"),
				key: TabKeys.HOURLY_RATE,
				content: (
					<>
						<Box className="fullCenter" data-testid="position-hours-field">
							<BoundHoursField
								label={t("hours")}
								boundedValue={hoursField}
								additionalProps={{ className: "w-100 left" }}
							/>
						</Box>
						<Box className="fullCenter" sx={{ mt: 2.5 }} data-testid="position-price-per-hour-field">
							<BoundEuroField
								label={t("price-per-hour")}
								boundedValue={pricePerHourField}
								additionalProps={{ className: "w-100 left" }}
							/>
						</Box>

						<Box className="fullCenter" sx={{ mt: 2.5 }}>
							<TextField
								value={formatCurrency(position.totalPrice) ?? "---"}
								label={t("volume")}
								className="w-100 left"
								disabled
							/>
						</Box>
					</>
				),
			},
			{
				label: t("fix-price"),
				key: TabKeys.FIXED_PRICE,
				content: (
					<>
						<Box className="fullCenter" data-testid="position-price-field">
							<BoundEuroField
								label={t("price")}
								boundedValue={priceField}
								additionalProps={{ className: "w-100 left" }}
							/>
						</Box>
						<Box className="fullCenter">
							<FormControl sx={{ mt: 1 }} fullWidth>
								<InputLabel className={errors?.priceRate ? "red" : ""} id="rate">
									{t("type")}
								</InputLabel>
								<Select
									labelId="rate"
									value={position.priceRate ?? ""}
									label={t("price-rate")}
									onChange={(event) => {
										setPosition({
											...position,
											priceRate: event.target.value ?? null,
										});
										setErrors({ ...errors, priceRate: "" });
									}}
									error={Boolean(errors?.priceRate)}>
									<MenuItem key="rate_daily" value={ProjectPositionPriceRateType.DailyRate}>
										{t("price-rate.daily_rate", { ns: "enum" })}
									</MenuItem>
									<MenuItem key="rate_flat" value={ProjectPositionPriceRateType.FlatRate}>
										{t("price-rate.flat_rate", { ns: "enum" })}
									</MenuItem>
									<MenuItem key="rate_monthly" value={ProjectPositionPriceRateType.MonthlyRate}>
										{t("price-rate.monthly_rate", { ns: "enum" })}
									</MenuItem>
									<MenuItem key="rate_unit" value={ProjectPositionPriceRateType.Unit}>
										{t("price-rate.unit", { ns: "enum" })}
									</MenuItem>
									<MenuItem key="rate_yearly" value={ProjectPositionPriceRateType.YearlyRate}>
										{t("price-rate.yearly_rate", { ns: "enum" })}
									</MenuItem>
								</Select>
								{errors?.priceRate ? (
									<FormHelperText className="red">{errors.priceRate}</FormHelperText>
								) : null}
							</FormControl>
						</Box>
					</>
				),
			},
		];

		return (
			<Dialog open={open} maxWidth="sm" fullWidth data-testid="position-dialog">
				<DialogTitle>{position?.id ? t("edit-position") : t("new-position")}</DialogTitle>
				<DialogContent>
					<Box className="fullCenter" sx={{ mt: 2.5 }}>
						<BoundAsyncSelect
							label={t("service")}
							boundedValue={serviceField}
							searchHook={availableServices}
							additionalProps={{ className: "w-100 left" }}
						/>
					</Box>
					<Box className="fullCenter" sx={{ mt: 2.5 }}>
						<TextField
							value={formatCurrency(position.service?.price) ?? "---"}
							label={t("hourly-rate")}
							className="w-100 left"
							disabled
						/>
					</Box>
					<Box className="fullCenter" sx={{ mt: 2.5 }}>
						<BoundTextField
							label={t("description")}
							boundedValue={descriptionField}
							additionalProps={{ className: "w-100 left", multiline: true, minRows: 3 }}
						/>
					</Box>
					<Box className="fullCenter" sx={{ mt: 2.5 }}>
						<BoundAsyncSelect
							label={t("billing-profile")}
							boundedValue={accountingProfileField}
							searchHook={searchAccountingProfileHook}
							additionalProps={{ className: "w-100 left" }}
						/>
					</Box>

					<Tabs tabIndex={tabIndex} setTabIndex={setTabIndex} tabs={allTabs} />
				</DialogContent>
				<DialogActions>
					<Button
						variant="outlined"
						onClick={handleClose}
						autoFocus
						disabled={loading}
						data-testid="position-cancel-button">
						{t("cancel")}
					</Button>
					<LoadingButton
						loading={loading}
						variant="contained"
						onClick={handleSubmit}
						className={loading ? "" : "blueButton"}
						data-testid="position-save-button">
						{t("save")}
					</LoadingButton>
				</DialogActions>
			</Dialog>
		);
	},
);

DialogProjectPosition.displayName = "DialogProjectPosition";
export { DialogProjectPosition };
